import {memo, useCallback, useMemo} from 'react'

import SvgIcon from '../../../../../../library/components/SvgIcon/SvgIcon'
import type {BuildId, UniqueRevisionId, VcsRootInstanceId, VcsLabel} from '../../../../../../types'
import BranchLabel from '../../../../../common/BranchLabel/BranchLabel'
import ChangeRevisionLink from '../../../../../packages/Changes/ChangeRevision/ChangeRevision'
import {getVcsRootInstance} from '../../../../../packages/Changes/Changes.selectors'
import {getRevision, makeGetVcsLabels} from '../ChangesRevisions.selectors'

import styles from './ChangesRepository.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId?: BuildId | null | undefined
  vcsRootInstanceId: VcsRootInstanceId | null | undefined
  revisionId: UniqueRevisionId
  personal?: boolean
}

function ChangesRevision({buildId, personal, vcsRootInstanceId, revisionId}: Props) {
  const getVcsLabel = useMemo(makeGetVcsLabels, [])
  const vcsRootId = useAppSelector(
    state => getVcsRootInstance(state, vcsRootInstanceId)?.['vcs-root'],
  )
  const vcsLabels = useAppSelector(state => getVcsLabel(state, buildId, vcsRootId))
  const version = useAppSelector(state => getRevision(state, revisionId)?.version)
  const vcsBranchName = useAppSelector(state => getRevision(state, revisionId)?.vcsBranchName)
  const renderVcsLabel = useCallback(
    (vcsLabel: VcsLabel) => (
      <div className={styles.vcsLabel} key={vcsLabel.text}>
        <SvgIcon className={styles.vcsLabelIcon} title="VCS label(tag)" icon="tag" />
        <span>{vcsLabel.text}</span>
      </div>
    ),
    [],
  )
  const renderVcsLabelErrorMessage = useCallback(
    (vcsLabel: VcsLabel) => (
      <div className={styles.vcsLabelsError} key={vcsLabel.text}>
        {vcsLabel.failureReason}
      </div>
    ),
    [],
  )
  const setVcsLabels = vcsLabels.filter(vcsLabel => vcsLabel.status === 'SUCCESSFUL_SET')
  const notSetVcsLabels = vcsLabels.filter(vcsLabel => vcsLabel.status === 'FAILED')
  return (
    <div className={styles.revision}>
      <div className={styles.revisionMainContent}>
        <div className={styles.branchAndLabels}>
          <div data-test-changes-revision-branch className={styles.vcsBranchName}>
            {vcsBranchName != null && <BranchLabel name={vcsBranchName} noLink />}
          </div>
          {setVcsLabels.length > 0 && setVcsLabels.map(renderVcsLabel)}
        </div>
        <div className={styles.hash}>
          <ChangeRevisionLink
            className={styles.version}
            copyButtonClassName={styles.versionCopyButton}
            version={version}
            personal={personal}
            withCopyButton
            withShortView
          />
        </div>
      </div>
      {notSetVcsLabels.length > 0 && notSetVcsLabels.map(renderVcsLabelErrorMessage)}
    </div>
  )
}

export default memo(ChangesRevision)
